import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createMedicalCase,
  fetchMyCases,
  fetchPublicCases,
  fetchCaseById,
  updateMedicalCase,
  deleteMedicalCase,
  addAttachments,
  deleteAttachment,
  createAnnotation,
  fetchAnnotations,
  updateAnnotation,
  deleteAnnotation,
  toggleCaseLike,
  fetchCaseLikes,
  createCaseComment,
  fetchCaseComments,
  updateCaseComment,
  deleteCaseComment,
  createLive,
  fetchMyLives,
  fetchPublicLives,
  fetchLiveById,
  updateLive,
  startLive,
  joinLive,
  leaveLive,
  endLive,
  cancelLive,
  createLiveComment,
  fetchLiveComments,
  updateLiveComment,
  deleteLiveComment,
} from "../redux/thunks/medicalCasesThunks";
import {
  selectMyUnpublishedCases,
  selectMyUnpublishedCasesStatus,
  selectMyUnpublishedCasesError,
  selectPublicCases,
  selectPublicCasesStatus,
  selectPublicCasesError,
  selectCurrentCase,
  selectCurrentCaseStatus,
  selectCurrentCaseError,
  selectCommentsByCaseId,
  selectCommentsStatus,
  selectCommentsError,
  selectLikesByCaseId,
  selectLikesStatus,
  selectAnnotationsByCaseAndAttachment,
  selectAnnotationsStatus,
  selectAnnotationsError,
  selectSuccessMessage,
  selectError,
  selectMyLives,
  selectMyLivesStatus,
  selectMyLivesError,
  selectPublicLives,
  selectPublicLivesStatus,
  selectPublicLivesError,
  selectCurrentLive,
  selectCurrentLiveStatus,
  selectCurrentLiveError,
  selectLiveCommentsById,
  selectLiveCommentsStatus,
  selectLiveCommentsError,
} from "../redux/selectors/medicalCasesSelectors";

/**
 * Hook pour gérer les cas médicaux
 */
export function useMedicalCases() {
  const dispatch = useDispatch();

  const myCases = useSelector(selectMyUnpublishedCases);
  const myStatus = useSelector(selectMyUnpublishedCasesStatus);
  const myError = useSelector(selectMyUnpublishedCasesError);

  const publicCases = useSelector(selectPublicCases);
  const publicStatus = useSelector(selectPublicCasesStatus);
  const publicError = useSelector(selectPublicCasesError);

  const currentCase = useSelector(selectCurrentCase);
  const caseStatus = useSelector(selectCurrentCaseStatus);
  const caseError = useSelector(selectCurrentCaseError);

  const successMessage = useSelector(selectSuccessMessage);
  const error = useSelector(selectError);
  const myLives = useSelector(selectMyLives);
  const myLivesStatus = useSelector(selectMyLivesStatus);
  const myLivesError = useSelector(selectMyLivesError);
  const publicLives = useSelector(selectPublicLives);
  const publicLivesStatus = useSelector(selectPublicLivesStatus);
  const publicLivesError = useSelector(selectPublicLivesError);
  const currentLive = useSelector(selectCurrentLive);
  const currentLiveStatus = useSelector(selectCurrentLiveStatus);
  const currentLiveError = useSelector(selectCurrentLiveError);
  const liveComments = useSelector((state) =>
    selectLiveCommentsById(state, currentLive?.id || null)
  );
  const liveCommentsStatus = useSelector(selectLiveCommentsStatus);
  const liveCommentsError = useSelector(selectLiveCommentsError);

  const fetchMyCasesCallback = useCallback(
    ({ page, limit } = {}) => {
      return dispatch(fetchMyCases({ page, limit }));
    },
    [dispatch]
  );

  const fetchPublicCasesCallback = useCallback(
    ({ page, limit } = {}) => {
      return dispatch(fetchPublicCases({ page, limit }));
    },
    [dispatch]
  );

  const fetchCaseByIdCallback = useCallback(
    (caseId) => {
      if (caseId) return dispatch(fetchCaseById(caseId));
    },
    [dispatch]
  );

  const createCaseCallback = useCallback(
    (caseData) => {
      return dispatch(createMedicalCase(caseData));
    },
    [dispatch]
  );

  const createLiveCallback = useCallback(
    (liveData) => {
      return dispatch(createLive(liveData));
    },
    [dispatch]
  );

  const fetchMyLivesCallback = useCallback(
    ({ page, limit } = {}) => {
      return dispatch(fetchMyLives({ page, limit }));
    },
    [dispatch]
  );

  const fetchPublicLivesCallback = useCallback(
    ({ page, limit } = {}) => {
      return dispatch(fetchPublicLives({ page, limit }));
    },
    [dispatch]
  );

  const fetchLiveByIdCallback = useCallback(
    (liveId) => {
      if (liveId) return dispatch(fetchLiveById(liveId));
    },
    [dispatch]
  );

  const updateLiveCallback = useCallback(
    (liveId, updates) => {
      return dispatch(updateLive({ liveId, updates }));
    },
    [dispatch]
  );

  const startLiveCallback = useCallback(
    (liveId) => {
      if (!liveId) return;
      return dispatch(startLive(liveId));
    },
    [dispatch]
  );

  const joinLiveCallback = useCallback(
    (liveId) => {
      if (!liveId) return;
      return dispatch(joinLive(liveId));
    },
    [dispatch]
  );

  const leaveLiveCallback = useCallback(
    (liveId) => {
      if (!liveId) return;
      return dispatch(leaveLive(liveId));
    },
    [dispatch]
  );

  const endLiveCallback = useCallback(
    (liveId) => {
      if (!liveId) return;
      return dispatch(endLive(liveId));
    },
    [dispatch]
  );

  const cancelLiveCallback = useCallback(
    (liveId) => {
      if (!liveId) return;
      return dispatch(cancelLive(liveId));
    },
    [dispatch]
  );

  const createLiveCommentCallback = useCallback(
    (liveId, content) => {
      if (!liveId || !content) return;
      return dispatch(createLiveComment({ liveId, content }));
    },
    [dispatch]
  );

  const fetchLiveCommentsCallback = useCallback(
    (liveId) => {
      if (!liveId) return;
      return dispatch(fetchLiveComments(liveId));
    },
    [dispatch]
  );

  const updateLiveCommentCallback = useCallback(
    (liveId, commentId, content) => {
      if (!liveId || !commentId || !content) return;
      return dispatch(updateLiveComment({ liveId, commentId, content }));
    },
    [dispatch]
  );

  const deleteLiveCommentCallback = useCallback(
    (liveId, commentId) => {
      if (!liveId || !commentId) return;
      return dispatch(deleteLiveComment({ liveId, commentId }));
    },
    [dispatch]
  );

  const updateCaseCallback = useCallback(
    (caseId, updates) => {
      return dispatch(updateMedicalCase({ caseId, updates }));
    },
    [dispatch]
  );

  const deleteCaseCallback = useCallback(
    (caseId) => {
      return dispatch(deleteMedicalCase(caseId));
    },
    [dispatch]
  );

  const addFilesCallback = useCallback(
    (caseId, files) => {
      return dispatch(addAttachments({ caseId, files }));
    },
    [dispatch]
  );

  const removeAttachmentCallback = useCallback(
    (caseId, attachmentId) => {
      return dispatch(deleteAttachment({ caseId, attachmentId }));
    },
    [dispatch]
  );

  return {
    myCases,
    myStatus,
    myError,
    publicCases,
    publicStatus,
    publicError,
    currentCase,
    caseStatus,
    caseError,
    myLives,
    myLivesStatus,
    myLivesError,
    publicLives,
    publicLivesStatus,
    publicLivesError,
    currentLive,
    currentLiveStatus,
    currentLiveError,
    liveComments,
    liveCommentsStatus,
    liveCommentsError,
    successMessage,
    error,
    fetchMyCases: fetchMyCasesCallback,
    fetchPublicCases: fetchPublicCasesCallback,
    fetchCaseById: fetchCaseByIdCallback,
    createCase: createCaseCallback,
    updateCase: updateCaseCallback,
    deleteCase: deleteCaseCallback,
    addFiles: addFilesCallback,
    removeAttachment: removeAttachmentCallback,
    createLive: createLiveCallback,
    fetchMyLives: fetchMyLivesCallback,
    fetchPublicLives: fetchPublicLivesCallback,
    fetchLiveById: fetchLiveByIdCallback,
    updateLive: updateLiveCallback,
    startLive: startLiveCallback,
    joinLive: joinLiveCallback,
    leaveLive: leaveLiveCallback,
    endLive: endLiveCallback,
    cancelLive: cancelLiveCallback,
    createLiveComment: createLiveCommentCallback,
    fetchLiveComments: fetchLiveCommentsCallback,
    updateLiveComment: updateLiveCommentCallback,
    deleteLiveComment: deleteLiveCommentCallback,
  };
}

/**
 * Hook pour gérer les commentaires des cas
 */
export function useCaseComments(caseId) {
  const dispatch = useDispatch();

  const comments = useSelector((state) => selectCommentsByCaseId(state, caseId));
  const isLoading = useSelector(selectCommentsStatus) === "loading";
  const error = useSelector(selectCommentsError);

  useEffect(() => {
    if (caseId) {
      dispatch(fetchCaseComments(caseId));
    }
  }, [caseId, dispatch]);

  const addComment = useCallback(
    (content) => {
      if (!caseId || !content) return;
      return dispatch(createCaseComment({ caseId, content }));
    },
    [caseId, dispatch]
  );

  const updateComment = useCallback(
    (commentId, content) => {
      if (!caseId || !commentId || !content) return;
      return dispatch(updateCaseComment({ caseId, commentId, content }));
    },
    [caseId, dispatch]
  );

  const deleteComment = useCallback(
    (commentId) => {
      if (!caseId || !commentId) return;
      return dispatch(deleteCaseComment({ caseId, commentId }));
    },
    [caseId, dispatch]
  );

  const refetchComments = useCallback(() => {
    if (caseId) {
      dispatch(fetchCaseComments(caseId));
    }
  }, [caseId, dispatch]);

  return {
    comments,
    isLoading,
    error,
    addComment,
    updateComment,
    deleteComment,
    refetchComments,
  };
}

/**
 * Hook pour gérer les likes des cas
 */
export function useCaseLikes(caseId) {
  const dispatch = useDispatch();

  const likes = useSelector((state) => selectLikesByCaseId(state, caseId));
  const isLoading = useSelector(selectLikesStatus) === "loading";

  useEffect(() => {
    if (caseId) {
      dispatch(fetchCaseLikes(caseId));
    }
  }, [caseId, dispatch]);

  const toggleLike = useCallback(() => {
    if (!caseId) return;
    return dispatch(toggleCaseLike(caseId));
  }, [caseId, dispatch]);

  const refetchLikes = useCallback(() => {
    if (caseId) {
      dispatch(fetchCaseLikes(caseId));
    }
  }, [caseId, dispatch]);

  return {
    likes,
    isLoading,
    toggleLike,
    refetchLikes,
  };
}

/**
 * Hook pour gérer les annotations
 *
 * Fixed to:
 *  - read from the Redux store (via selectors) instead of duplicating state
 *    locally with useState, so annotations stay in sync everywhere they're used
 *  - use `.unwrap()` so rejected thunks actually throw and populate `error`
 *    (dispatch() never rejects on its own with rejectWithValue-based thunks)
 *  - key annotations by (caseId, attachmentId) so switching attachments
 *    within the same case doesn't clobber previously loaded annotations
 */
export function useCaseAnnotations(caseId, attachmentId) {
  const dispatch = useDispatch();

  const annotations = useSelector((state) =>
    selectAnnotationsByCaseAndAttachment(state, caseId, attachmentId)
  );
  const isLoading = useSelector(selectAnnotationsStatus) === "loading";
  const error = useSelector(selectAnnotationsError);

  const fetchAnnotationsCallback = useCallback(async () => {
    if (!caseId || !attachmentId) return;
    try {
      await dispatch(fetchAnnotations({ caseId, attachmentId })).unwrap();
    } catch (err) {
      // error is already stored in Redux state via the rejected reducer;
      // swallow here so callers using this as a fire-and-forget refetch
      // don't need a try/catch of their own.
    }
  }, [caseId, attachmentId, dispatch]);

  useEffect(() => {
    fetchAnnotationsCallback();
  }, [fetchAnnotationsCallback]);

  const addAnnotation = useCallback(
    (annotation) => {
      if (!caseId || !attachmentId) return;
      return dispatch(
        createAnnotation({ caseId, attachmentId, annotation })
      ).unwrap();
    },
    [caseId, attachmentId, dispatch]
  );

  const updateAnnotationCallback = useCallback(
    (annotationId, updates) => {
      if (!caseId || !attachmentId || !annotationId) return;
      return dispatch(
        updateAnnotation({ caseId, attachmentId, annotationId, updates })
      ).unwrap();
    },
    [caseId, attachmentId, dispatch]
  );

  const deleteAnnotationCallback = useCallback(
    (annotationId) => {
      if (!caseId || !attachmentId || !annotationId) return;
      return dispatch(
        deleteAnnotation({ caseId, attachmentId, annotationId })
      ).unwrap();
    },
    [caseId, attachmentId, dispatch]
  );

  return {
    annotations,
    isLoading,
    error,
    addAnnotation,
    updateAnnotation: updateAnnotationCallback,
    deleteAnnotation: deleteAnnotationCallback,
    refetch: fetchAnnotationsCallback,
  };
}
